
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";

import Loader from "../../components/Loader";
import { buildCard, deleteCard, duplicateCard, fetchCardFile, getCardMeta, listCards } from "./printCardApi";
import { readApiError } from "./printSignApi";
import { formatBytes, formatDateTime } from "./signUi";
import { ActionsMenu, PageMessage, SignsPage, StatusBadge } from "./signPage";
import { CARDS_ROOT } from "./PrintCardEditorPage";

/*
 * Визитки: список, как у вывесок и смет. Отсюда — редактор, сборка, файлы, копия, удаление.
 */

const fileName = (card, file, format) => (
    `${(card.name || "Визитка").replace(/[\\/:*?"<>|]+/g, " ").trim()} ${file.widthMm}x${file.heightMm}${format === "sheet" ? " лист A4" : ""}.${format === "png" ? "png" : "pdf"}`
);

const PrintCardsListPage = () => {
    const navigate = useNavigate();
    const [cards, setCards] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [busy, setBusy] = useState({});
    const [expanded, setExpanded] = useState("");
    const [message, setMessage] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [nextCards, nextMeta] = await Promise.all([listCards(), getCardMeta()]);
            setCards(nextCards);
            setMeta(nextMeta);
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, "Не удалось загрузить визитки") });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const designLabel = (key) => (meta?.designs || []).find((design) => design.key === key)?.label || "Гранит и золото";
    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return cards;
        return cards.filter((card) => [card.name, card.profileName, card.person?.name, card.person?.position]
            .some((value) => String(value || "").toLowerCase().includes(needle)));
    }, [cards, query]);

    const run = async (card, label, task, errorText) => {
        setBusy((current) => ({ ...current, [card._id]: label }));
        setMessage(null);
        try {
            await task();
        } catch (error) {
            setMessage({ type: "error", text: await readApiError(error, errorText) });
        } finally {
            setBusy((current) => {
                const next = { ...current };
                delete next[card._id];
                return next;
            });
        }
    };

    const replace = (next) => setCards((current) => current.map((item) => (item._id === next._id ? { ...item, ...next } : item)));

    const build = (card) => run(card, "Собираем…", async () => {
        const next = await buildCard(card._id);
        replace(next);
        setMessage({ type: "success", text: `«${next.name}» собрана: PDF для типографии, лист A4 и PNG.` });
    }, "Не удалось собрать визитку");

    const openFile = (card, file, format) => {
        const win = window.open("", "_blank");
        run(card, "Открываем…", async () => {
            try {
                const url = URL.createObjectURL(await fetchCardFile(card._id, file._id, format));
                if (win) win.location.href = url;
                else window.open(url, "_blank");
            } catch (error) {
                win?.close();
                throw error;
            }
        }, "Не удалось открыть файл");
    };

    const downloadFile = (card, file, format) => run(card, "Скачиваем…", async () => {
        saveAs(await fetchCardFile(card._id, file._id, format), fileName(card, file, format));
    }, "Не удалось скачать файл");

    const copy = (card) => run(card, "Копируем…", async () => {
        const next = await duplicateCard(card._id);
        setCards((current) => [next, ...current]);
    }, "Не удалось скопировать");

    const remove = (card) => {
        if (!window.confirm(`Удалить визитку «${card.name}»? Собранные файлы тоже удалятся.`)) return;
        run(card, "Удаляем…", async () => {
            await deleteCard(card._id);
            setCards((current) => current.filter((item) => item._id !== card._id));
        }, "Не удалось удалить");
    };

    return (
        <SignsPage title="Визитки" subtitle="Двусторонние визитки из профиля бренда: PDF для типографии, лист A4 и PNG" back="/">
            <div className="signsPage__toolbar">
                <label className="signsSearch">
                    <span aria-hidden="true">⌕</span>
                    <input type="text" value={query} placeholder="Поиск по названию, сотруднику, профилю…" onChange={(event) => setQuery(event.target.value)} />
                </label>
                <button type="button" className="signsButton" onClick={() => navigate("/admin/print-signs")}>Вывески</button>
                <button type="button" className="signsButton" onClick={() => navigate("/admin/print-signs/profiles")}>Профили бренда</button>
                <button type="button" className="signsButton" onClick={() => navigate("/admin/print-signs/library")}>Библиотека картинок</button>
                <button type="button" className="signsButton is-primary" onClick={() => navigate(`${CARDS_ROOT}/new`)}>+ Новая визитка</button>
            </div>

            <PageMessage message={message} onClose={() => setMessage(null)} />

            <div className="signsTable">
                <div className="signsTable__row is-head">
                    <span>#</span>
                    <span>Название</span>
                    <span>Сотрудник</span>
                    <span>Размер · дизайн</span>
                    <span>Обновлено</span>
                    <span>Статус</span>
                    <span className="is-right">Действия</span>
                </div>
                {loading ? <div className="signsTable__empty"><Loader /></div> : null}
                {!loading && !filtered.length ? (
                    <div className="signsTable__empty">{cards.length ? "Ничего не нашлось." : "Визиток пока нет — нажмите «+ Новая визитка»."}</div>
                ) : null}
                {filtered.map((card, index) => {
                    const last = card.files[0];
                    const isOpen = expanded === card._id;
                    const vertical = card.size.orientation === "vertical";
                    return (
                        <div key={card._id} className={`signsTable__group${isOpen ? " is-open" : ""}`}>
                            <div className="signsTable__row">
                                <span className="signsTable__num">{index + 1}</span>
                                <button type="button" className="signsTable__name" onClick={() => navigate(`${CARDS_ROOT}/${card._id}`)}>
                                    <i className={`printSigns__swatch is-${card.design}`} />
                                    <strong>{card.name || "Визитка"}</strong>
                                </button>
                                <span className="signsTable__muted">
                                    {card.person?.name ? `${card.person.name}${card.person.position ? ` · ${card.person.position}` : ""}` : `фирма${card.profileName ? ` · ${card.profileName}` : ""}`}
                                </span>
                                <span className="signsTable__muted">
                                    {vertical ? `${card.size.heightMm} × ${card.size.widthMm}` : `${card.size.widthMm} × ${card.size.heightMm}`} мм · {designLabel(card.design)}
                                </span>
                                <span>{formatDateTime(card.updatedAt).split(",")[0] || "—"}</span>
                                <span>{last ? <StatusBadge tone="success">собрана</StatusBadge> : <StatusBadge>черновик</StatusBadge>}</span>
                                <span className="is-right">
                                    <ActionsMenu
                                        busy={busy[card._id]}
                                        disabled={!!busy[card._id]}
                                        items={[
                                            { label: "Открыть редактор", onClick: () => navigate(`${CARDS_ROOT}/${card._id}`) },
                                            { label: last ? "Собрать заново" : "Собрать PDF", onClick: () => build(card) },
                                            last && { label: "Открыть PDF для типографии", divider: true, onClick: () => openFile(card, last, "pdf") },
                                            last && { label: "Скачать PDF для типографии", onClick: () => downloadFile(card, last, "pdf") },
                                            last?.hasSheet && { label: "Скачать лист A4 (печать в офисе)", onClick: () => downloadFile(card, last, "sheet") },
                                            last?.hasPng && { label: "Скачать PNG для мессенджеров", onClick: () => downloadFile(card, last, "png") },
                                            card.files.length && { label: isOpen ? "Скрыть файлы" : "Показать все файлы", onClick: () => setExpanded(isOpen ? "" : card._id) },
                                            { label: "Сделать копию", divider: true, onClick: () => copy(card) },
                                            { label: "Удалить", danger: true, divider: true, onClick: () => remove(card) },
                                        ]}
                                    />
                                </span>
                            </div>
                            {isOpen ? (
                                <div className="signsFiles">
                                    <div className="signsFiles__title">Файлы ({card.files.length})</div>
                                    {card.files.map((file) => (
                                        <div key={file._id} className="signsFiles__row">
                                            <div>
                                                <strong>{fileName(card, file, "pdf")}</strong>
                                                <span>{formatDateTime(file.createdAt)} · PDF {formatBytes(file.pdfBytes)}{file.perSheet ? ` · на A4 — ${file.perSheet} шт.` : ""} · Собрал: {file.createdByName || "—"}</span>
                                            </div>
                                            <div className="signsFiles__actions">
                                                <button type="button" className="signsButton" onClick={() => openFile(card, file, "pdf")}>Открыть</button>
                                                <button type="button" className="signsButton" onClick={() => downloadFile(card, file, "pdf")}>PDF</button>
                                                {file.hasSheet ? <button type="button" className="signsButton" onClick={() => downloadFile(card, file, "sheet")}>Лист A4</button> : null}
                                                {file.hasPng ? <button type="button" className="signsButton" onClick={() => downloadFile(card, file, "png")}>PNG</button> : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </SignsPage>
    );
};

export default PrintCardsListPage;
